import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { GroupBase } from "react-select";
import type { LoadOptions } from "react-select-async-paginate";
import { usersApi } from "../../../../features/usersApi";
import { MadfarmPaginateSelect } from "../../../ui/molecules/paginateSelect";
import { Additional, OptionType, SelectValue } from "../../../ui/molecules/paginateSelect/types";
import { UserDTO, UserRole } from "../dto";

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_PAGE = 0;
const DEBOUNCE_DELAY = 300;

type UserOption = OptionType<string>;

interface BaseProps {
    role: UserRole;
    id: string;
    disabled?: boolean;
    required?: boolean;
    isClearable?: boolean;
    label?: string;
    placeholder?: string;
    error?: boolean;
    keepOverflow?: boolean;
    errorLabel?: string;
    menuPortalTargetIsNotBody?: boolean;
    filterUsers?: (users: UserDTO[]) => UserDTO[];

}

interface SingleSelectProps extends BaseProps {
    isMulti?: false;
    value?: string | { label: string; value: string } | null;
    onChange: (value?: string | null) => void;
}

interface MultiSelectProps extends BaseProps {
    isMulti: true;
    value?: (string | { label: string; value: string })[];
    onChange: (value?: string[]) => void;
}

type Props = SingleSelectProps | MultiSelectProps;

export function SelectUser(props: Props) {
    const [triggerFindAll] = usersApi.useLazyFindAllQuery();
    const isMulti = props.isMulti ?? false;

    const [internalOptionsMap, setInternalOptionsMap] = useState<Map<string, UserOption>>(new Map());
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const hasFetchedMissingIds = useRef<Set<string>>(new Set());

    const { data: backgroundData } = usersApi.useFindAllQuery({
        role: props.role,
        page: DEFAULT_PAGE,
        itemsPerPage: DEFAULT_PAGE_SIZE,
        order: false,
        sort: "createdAt"
    }, {
        refetchOnFocus: true,
        refetchOnReconnect: true,
    });

    const createUserOption = useCallback((user: UserDTO): UserOption => ({
        label: `${user.name} ${user.surname}${user.companyName ? ` - ${user.companyName}` : ""}`,
        value: user.id,
    }), []);

    useEffect(() => {
        if (backgroundData?.data) {
            let filteredUsers = backgroundData.data.map(({ user }) => user);
            if (props.filterUsers) {
                filteredUsers = props.filterUsers(filteredUsers);
            }

            setInternalOptionsMap((prev) => {
                const updated = new Map(prev);
                filteredUsers.forEach((user) => {
                    updated.set(user.id, createUserOption(user));
                });
                return updated;
            });
        }
    }, [backgroundData, props.filterUsers, createUserOption]);

    useEffect(() => {
        const missingIds: string[] = [];

        if (isMulti) {
            const multiProps = props as MultiSelectProps;
            const ids = multiProps.value || [];

            ids.forEach(id => {
                if (id && typeof id === "string" && !internalOptionsMap.has(id) && !hasFetchedMissingIds.current.has(id)) {
                    missingIds.push(id);
                }
            });
        } else {
            const singleProps = props as SingleSelectProps;
            const id = singleProps.value;

            if (id && typeof id === "string" && !internalOptionsMap.has(id) && !hasFetchedMissingIds.current.has(id)) {
                missingIds.push(id);
            }
        }

        if (missingIds.length > 0) {
            missingIds.forEach(id => hasFetchedMissingIds.current.add(id));

            triggerFindAll({
                role: props.role,
                page: DEFAULT_PAGE,
                itemsPerPage: DEFAULT_PAGE_SIZE,
                order: true,
            });
        }
    }, [props, isMulti, triggerFindAll]);

    const currentValue = useMemo(() => {
        if (isMulti) {
            const multiProps = props as MultiSelectProps;
            const ids = multiProps.value || [];
            return ids.map(id => {
                if (id) {
                    if (typeof id === "string")
                        return internalOptionsMap.get(id)
                    else {
                        return id;
                    }
                }
                return { label: `User ${id}`, value: id };
            });
        } else {
            const singleProps = props as SingleSelectProps;
            const id = singleProps.value;
            if (!id) return null;
            else if (typeof id !== "string") return id;
            else {
                return internalOptionsMap.get(id) || { label: `User ${id}`, value: id };
            }
        }
    }, [props, internalOptionsMap, isMulti]);

    const loadOptions: LoadOptions<UserOption, GroupBase<UserOption>, Additional> = useCallback(
        async (search, _, additional) => {
            return new Promise((resolve) => {
                if (debounceTimerRef.current) {
                    clearTimeout(debounceTimerRef.current);
                }

                debounceTimerRef.current = setTimeout(async () => {
                    try {
                        if (!search && (!additional?.page || additional.page === DEFAULT_PAGE)) {
                            const cachedOptions = Array.from(internalOptionsMap.values());
                            if (cachedOptions.length > 0) {
                                resolve({
                                    options: cachedOptions,
                                    hasMore: true,
                                    additional: {
                                        page: DEFAULT_PAGE + 1,
                                    },
                                });
                                return;
                            }
                        }

                        const response = await triggerFindAll({
                            role: props.role,
                            page: additional?.page || DEFAULT_PAGE,
                            itemsPerPage: DEFAULT_PAGE_SIZE,
                            order: false,
                            sort: "createdAt",
                            search: search || undefined,
                        }).unwrap();

                        let filteredUsers = response.data.map(({ user }) => user);
                        if (props.filterUsers) {
                            filteredUsers = props.filterUsers(filteredUsers);
                        }

                        const options: UserOption[] = filteredUsers.map(createUserOption);

                        setInternalOptionsMap((prev) => {
                            const updated = new Map(prev);
                            options.forEach(option => {
                                updated.set(option.value, option);
                            });
                            return updated;
                        });

                        resolve({
                            options,
                            hasMore: response.next,
                            additional: {
                                page: (additional?.page || DEFAULT_PAGE) + 1,
                            },
                        });
                    } catch (error) {
                        resolve({
                            options: [],
                            hasMore: false,
                            additional: {
                                page: DEFAULT_PAGE,
                            },
                        });
                    }
                }, search ? DEBOUNCE_DELAY : 0);
            });
        },
        [props.role, props.filterUsers, triggerFindAll, internalOptionsMap, createUserOption, props.id]
    );

    const handleChange = useCallback(
        (selectedOption: SelectValue<string, typeof isMulti>) => {
            if (isMulti) {
                const multiProps = props as MultiSelectProps;
                const selectedArray = selectedOption as UserOption[];
                const ids = selectedArray?.map(option => option.value) || [];
                multiProps.onChange(ids);
            } else {
                const singleProps = props as SingleSelectProps;
                const selectedSingle = selectedOption as UserOption | null;
                const id = selectedSingle?.value || null;
                singleProps.onChange(id);
            }
        },
        [props, isMulti]
    );

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const selectProps = useMemo(() => {
        const baseProps = {
            required: props.required,
            key: props.id,
            disabled: props.disabled,
            menuPortalTargetIsNotBody: props.menuPortalTargetIsNotBody,
            label: props.label,
            loadOptions,
            isClearable: props.isClearable ?? true,
            placeholder: props.placeholder || (isMulti ? "Cerca e seleziona utenti" : "Cerca e seleziona un utente"),
            error: props.error || false,
            errorLabel: props.errorLabel || "Errore durante la selezione",
            noOptionsMessage: "Non è stato trovato nessun risultato",
            onChange: handleChange,
            keepOverflow: props.keepOverflow,
        };

        if (isMulti) {
            return {
                ...baseProps,
                isMulti: true as const,
                value: currentValue as UserOption[] | undefined,
                hideSelectedOptions: false,
            };
        } else {
            return {
                ...baseProps,
                isMulti: false as const,
                value: currentValue as UserOption | null | undefined,
            };
        }
    }, [
        props.required,
        props.id,
        props.disabled,
        props.label,
        props.placeholder,
        props.error,
        props.errorLabel,
        props.isClearable,
        props.keepOverflow,
        currentValue,
        loadOptions,
        handleChange,
        isMulti,
    ]);

    if (isMulti) {
        return (
            <MadfarmPaginateSelect<string, true>
                {...(selectProps as any)}
            />
        );
    } else {
        return (
            <MadfarmPaginateSelect<string, false>
                {...(selectProps as any)}
            />
        );
    }
}
